/**
 * Lightweight swipe-to-reveal actions for mobile delivery cards.
 * No external gesture library — touch translate only.
 */

import React, { useRef, useState } from "react";
import styled from "styled-components";

const Shell = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  margin-bottom: 12px;
`;

const ActionsUnderlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: ${(props) =>
    props.$side === "left" ? "flex-start" : "flex-end"};
  background: ${(props) => (props.$side === "left" ? "#fef2f2" : "#ecfdf5")};
  z-index: 0;
`;

const ActionBtn = styled.button`
  border: none;
  min-width: 76px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: ${(props) => props.$bg || "#005249"};
  cursor: pointer;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  background: transparent;
  transition: transform 0.18s ease;
  touch-action: pan-y;
`;

const OPEN_PX = 156;
const THRESHOLD = 48;

/**
 * @param {{
 *   children: React.ReactNode,
 *   leftActions?: Array<{ key: string, label: string, bg?: string, onClick: () => void }>,
 *   rightActions?: Array<{ key: string, label: string, bg?: string, onClick: () => void }>,
 * }} props
 */
export default function SwipeableDeliveryRow({
  children,
  leftActions = [],
  rightActions = [],
}) {
  const startX = useRef(0);
  const startY = useRef(0);
  const locking = useRef(null); // 'h' | 'v' | null
  const [offset, setOffset] = useState(0);

  const maxLeft = leftActions.length ? OPEN_PX : 0;
  const maxRight = rightActions.length ? OPEN_PX : 0;
  const side = offset > 0 ? "left" : offset < 0 ? "right" : null;
  const visibleActions = side === "left" ? leftActions : rightActions;

  const onTouchStart = (e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    locking.current = null;
  };

  const onTouchMove = (e) => {
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    if (!locking.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      locking.current = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
    }
    if (locking.current !== "h") return;
    e.preventDefault();
    let next = dx;
    if (next > 0) next = Math.min(next, maxLeft);
    if (next < 0) next = Math.max(next, -maxRight);
    setOffset(next);
  };

  const onTouchEnd = () => {
    if (locking.current !== "h") {
      setOffset(0);
      return;
    }
    if (offset > THRESHOLD) setOffset(maxLeft);
    else if (offset < -THRESHOLD) setOffset(-maxRight);
    else setOffset(0);
  };

  const runAction = (action) => {
    setOffset(0);
    action.onClick?.();
  };

  return (
    <Shell>
      {side && visibleActions.length > 0 && (
        <ActionsUnderlay $side={side}>
          {visibleActions.map((action) => (
            <ActionBtn
              key={action.key}
              type="button"
              $bg={action.bg}
              onClick={() => runAction(action)}
            >
              {action.label}
            </ActionBtn>
          ))}
        </ActionsUnderlay>
      )}
      <Content
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={() => setOffset(0)}
      >
        {children}
      </Content>
    </Shell>
  );
}
